import { Box, Flex, Stack, Text } from '@mantine/core';
import { FlowNavigationFooter } from 'src/design-system/FlowNavigationFooter';
import { Select } from 'src/design-system/Select';
import { useState } from 'react';
import { Client } from 'src/admin/users/types';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { useFamilyRoles } from '../hooks/useFamilyRoles';
import { V2MemberRoles } from './dataHelpers';
import FamilyNameHeader from './FamilyNameHeader';

type Props = {
  selectedMembers: Client[];
  onBack: () => void;
  onCancel: () => void;
  onSubmit: (familyName: string, roles: V2MemberRoles) => void;
  isLoading: boolean;
  showAdvisorPicker?: boolean;
  advisorUserId?: string;
  onAdvisorChange?: (value: string | null) => void;
  initialFamilyName?: string;
  initialRoles?: V2MemberRoles;
  actionLabel?: string;
  title?: string;
  rolesEditable?: boolean;
};

export default function NameAndRolesStep({
  selectedMembers,
  onBack,
  onCancel,
  onSubmit,
  isLoading,
  showAdvisorPicker,
  advisorUserId,
  onAdvisorChange,
  initialFamilyName = '',
  initialRoles = {},
  actionLabel,
  title = 'Set up this family',
  rolesEditable = true,
}: Props) {
  const { familyRoles, isLoading: rolesLoading, error: rolesError } = useFamilyRoles();
  const [familyName, setFamilyName] = useState(initialFamilyName);
  const [roles, setRoles] = useState<V2MemberRoles>(initialRoles);

  const roleOptions = familyRoles.map(role => ({ value: role.id, label: role.name }));
  const rolesUnavailable = rolesLoading || !!rolesError;
  const rolePlaceholder = rolesError ? 'Roles unavailable' : 'Select role';

  const isValid = familyName.trim().length > 0;

  return (
    <Flex direction="column" h="100%">
      <FlowLayout
        header={
          <FamilyNameHeader
            title={title}
            familyName={familyName}
            onFamilyNameChange={setFamilyName}
            onCancel={onCancel}
            showAdvisorPicker={showAdvisorPicker}
            advisorUserId={advisorUserId}
            onAdvisorChange={onAdvisorChange}
          />
        }
        body={
          <FlowBody
            footerAlign="center"
            footer={
              <FlowNavigationFooter
                onBack={onBack}
                onAction={() => onSubmit(familyName, roles)}
                actionDisabled={!isValid}
                actionLoading={isLoading}
                actionLabel={actionLabel}
              />
            }
          >
            <Box px="xs" pb="md">
              <Stack gap="sm">
                {selectedMembers.map(client => (
                  <Box key={client.id} w="100%" bg="gray.0" style={{ borderRadius: 20 }}>
                    <Flex justify="space-between" align="center">
                      <Box pl="md" py="lg">
                        <Text fw={FONT_WEIGHTS.semibold} size="md" c="gray.10">
                          {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
                        </Text>
                        {client.emailAddress && (
                          <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.7">
                            {client.emailAddress}
                          </Text>
                        )}
                      </Box>

                      <Box pr="md" py="sm" miw="50%">
                        <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.7" mb={6}>
                          Family role
                        </Text>
                        {rolesEditable ? (
                          <Select
                            placeholder={rolePlaceholder}
                            data={roleOptions}
                            value={roles[client.id] ?? null}
                            onChange={val => setRoles(prev => ({ ...prev, [client.id]: val }))}
                            disabled={rolesUnavailable}
                            clearable
                            variant="card"
                            w="100%"
                            styles={{ input: { height: 'auto', minHeight: 'unset' } }}
                          />
                        ) : (
                          <Text size="md" fw={FONT_WEIGHTS.medium} c="gray.10">
                            {roleOptions.find(option => option.value === roles[client.id])?.label ??
                              '—'}
                          </Text>
                        )}
                      </Box>
                    </Flex>
                  </Box>
                ))}
              </Stack>
            </Box>
          </FlowBody>
        }
      />
    </Flex>
  );
}
